'use client'

import { useState } from 'react'
import Image from 'next/image'
import { cn } from '@/lib/utils'

interface ImageWithFallbackProps {
  src: string | null | undefined
  alt: string
  fill?: boolean
  width?: number
  height?: number
  className?: string
  fallbackSrc?: string
  fallbackText?: string
  sizes?: string
  priority?: boolean
}

export function ImageWithFallback({
  src,
  alt,
  fill = false,
  width,
  height,
  className,
  fallbackSrc = '/placeholder.svg',
  fallbackText,
  sizes,
  priority = false,
}: ImageWithFallbackProps) {
  const [hasError, setHasError] = useState(false)

  // Determine the actual source
  const imageSrc = !src || src === '' || hasError
    ? (fallbackText ? `/placeholder.svg?text=${encodeURIComponent(fallbackText)}` : fallbackSrc)
    : src

  // If we have fallback text, use placeholder service
  const displaySrc = fallbackText && (imageSrc === fallbackSrc || !src)
    ? `https://placehold.co/400x400/6366f1/ffffff?text=${encodeURIComponent(fallbackText.slice(0, 2))}`
    : imageSrc

  if (fill) {
    return (
      <Image
        src={displaySrc}
        alt={alt}
        fill
        className={cn('object-cover', className)}
        sizes={sizes}
        priority={priority}
        onError={() => setHasError(true)}
      />
    )
  }

  return (
    <Image
      src={displaySrc}
      alt={alt}
      width={width || 400}
      height={height || 400}
      className={className}
      sizes={sizes}
      priority={priority}
      onError={() => setHasError(true)}
    />
  )
}

// Helper component for product images specifically
interface ProductImageProps {
  images: string | string[] | null | undefined
  alt: string
  fill?: boolean
  width?: number
  height?: number
  className?: string
  sizes?: string
  priority?: boolean
}

export function ProductImage({
  images,
  alt,
  fill = false,
  width,
  height,
  className,
  sizes,
  priority = false,
}: ProductImageProps) {
  const [hasError, setHasError] = useState(false)

  // Parse images - can be JSON string or array
  let imageArray: string[] = []
  if (images) {
    if (typeof images === 'string') {
      try {
        imageArray = JSON.parse(images)
      } catch {
        imageArray = [images]
      }
    } else if (Array.isArray(images)) {
      imageArray = images
    }
  }

  const firstImage = imageArray[0]
  const displaySrc = !firstImage || firstImage === '' || hasError
    ? '/placeholder.svg'
    : firstImage

  if (fill) {
    return (
      <Image
        src={displaySrc}
        alt={alt}
        fill
        className={cn('object-cover', className)}
        sizes={sizes}
        priority={priority}
        onError={() => setHasError(true)}
      />
    )
  }

  return (
    <Image
      src={displaySrc}
      alt={alt}
      width={width || 400}
      height={height || 400}
      className={className}
      sizes={sizes}
      priority={priority}
      onError={() => setHasError(true)}
    />
  )
}